/**
 * Documentation API — request contracts.
 *
 *   GET    /v1/docs/navigation                  (no auth; widens with the session)
 *   GET    /v1/docs/page?slug=…                 (no auth; widens with the session)
 *   GET    /v1/docs/shared/:shareToken          (no auth; the TOKEN is the authorization)
 *   GET    /v1/admin/docs/pages                 (super-admin)
 *   POST   /v1/admin/docs/pages                 (super-admin)
 *   GET    /v1/admin/docs/pages/:docId          (super-admin)
 *   PATCH  /v1/admin/docs/pages/:docId/draft    (super-admin)
 *   POST   /v1/admin/docs/pages/:docId/publish  (super-admin)
 *   POST   /v1/admin/docs/pages/:docId/unpublish (super-admin)
 *   POST   /v1/admin/docs/pages/:docId/share-token (super-admin)
 *
 * Documentation is platform-global — no route carries a school public id, and
 * none should be added. See the header of `apps/backend/prisma/schema/docs.prisma`.
 */

import { z } from 'zod'

import {
  DocSectionOrderSchema,
  DocSectionSchema,
  DocShareTokenSchema,
  DocSlugSchema,
  DocVersionStatusSchema,
  DocVisibilitySchema
} from '../../schemas/entities/doc'
import { DocsContentSchema } from '../../schemas/entities/docs-content'

const DocIdParam = z.object({ docId: z.string().min(1) })

// ============================================
// Reader navigation (no auth)
// ============================================

/**
 * Deliberately EMPTY. The navigation read takes no filter input: what a viewer
 * may see is decided server-side from their session, never from a query
 * parameter. A `visibility` filter here would be an authorization control the
 * client owns.
 */
export const GetDocsNavigationQuerySchema = z.object({}).strict()
export type GetDocsNavigationQuery = z.infer<typeof GetDocsNavigationQuerySchema>

/**
 * ONE published page, addressed by its reader path (U9).
 *
 * The slug travels as a QUERY parameter rather than a path suffix because a doc
 * slug holds the FULL multi-segment path (`communications/sending-a-message`).
 * A path-suffix route would need a wildcard segment, and the encoded-slash form
 * that avoids one is normalized by proxies differently in different places —
 * neither is a thing to make a public URL depend on.
 *
 * `.strict()` and one key: what a viewer may open is decided from their session,
 * NEVER from a parameter. A client cannot send `visibility` here for the same
 * reason it cannot send it to the navigation read.
 */
export const GetDocPageQuerySchema = z.object({ slug: DocSlugSchema }).strict()
export type GetDocPageQuery = z.infer<typeof GetDocPageQuerySchema>

/**
 * ONE published link-shareable page, addressed by its token (U9, R14).
 *
 * The token is BOTH the address and the authorization — there is nothing else
 * to check, which is why the route it serves takes no session at all. A page on
 * any other visibility tier does not answer here even if its token were known,
 * so a stale token from before a tier change resolves to nothing.
 */
export const GetSharedDocParamsSchema = z.object({ shareToken: DocShareTokenSchema }).strict()
export type GetSharedDocParams = z.infer<typeof GetSharedDocParamsSchema>

// ============================================
// Admin reads
// ============================================

export const ListDocsQuerySchema = z.object({
  /** Omitted = every page regardless of status. */
  status: DocVersionStatusSchema.optional(),
  /** Omitted = every section. */
  section: DocSectionSchema.optional()
})
export type ListDocsQuery = z.infer<typeof ListDocsQuerySchema>

export const GetDocParamsSchema = DocIdParam
export type GetDocParams = z.infer<typeof GetDocParamsSchema>

// ============================================
// Admin writes
// ============================================

/**
 * `visibility` is REQUIRED on create, not defaulted (U9). The column defaults to
 * PRIVATE as fail-closed insurance for a row written by a path that forgets it —
 * the product behaviour is that the author chooses, and a create that silently
 * picked for them is exactly the drift the current corpus is in (31 of 37 MDX
 * files omit the frontmatter key and are login-gated by accident).
 */
export const CreateDocRequestSchema = z
  .object({
    title: z.string().min(1).max(255),
    slug: DocSlugSchema,
    description: z.string().max(500).optional(),
    visibility: DocVisibilitySchema,
    section: DocSectionSchema,
    sectionOrder: DocSectionOrderSchema.optional()
  })
  .strict()
export type CreateDocRequest = z.infer<typeof CreateDocRequestSchema>

export const UpdateDocDraftParamsSchema = DocIdParam
export type UpdateDocDraftParams = z.infer<typeof UpdateDocDraftParamsSchema>

/**
 * A PARTIAL write (KTD8). Every key is optional and an ABSENT key is left
 * exactly as stored — the backend merges rather than replacing the row, so a
 * client that knows about fewer fields than the server cannot blank the ones it
 * has never heard of. A prior converter shipped client-side full-payload
 * responsibility and silently wiped unknown keys on save; that is the failure
 * this shape exists to prevent.
 *
 * `null` is therefore NOT the same as absent: `description: null` clears the
 * field, `description` omitted leaves it. Only nullable fields accept it.
 */
export const UpdateDocDraftRequestSchema = z
  .object({
    title: z.string().min(1).max(255).optional(),
    // Editable ONLY while the page has never been published. Once a PUBLISHED
    // version exists the write is rejected outright rather than silently
    // ignored, so a stale client gets a clear error instead of a no-op — no
    // live /docs/<slug> URL can break.
    slug: DocSlugSchema.optional(),
    description: z.string().max(500).nullable().optional(),
    visibility: DocVisibilitySchema.optional(),
    section: DocSectionSchema.optional(),
    sectionOrder: DocSectionOrderSchema.optional(),
    // Omitted for a metadata-only edit (a rename, a re-section); the backend
    // only writes a version row when real content is present, so moving a page
    // in the navigation does not look like an edit in the admin list.
    content: DocsContentSchema.optional()
  })
  .strict()
export type UpdateDocDraftRequest = z.infer<typeof UpdateDocDraftRequestSchema>

export const PublishDocParamsSchema = DocIdParam
export type PublishDocParams = z.infer<typeof PublishDocParamsSchema>

/**
 * HTML rendered from a draft's blocks by the web tier (the backend has no React
 * and never imports `@blocknote/*`). Optional: a caller that cannot render still
 * publishes, and the version row simply stores no HTML cache. NEVER trusted as
 * received — `services/docs/render/sanitize-docs-html.ts` stands between this
 * field and storage, and block JSON stays the source of truth regardless.
 *
 * Declared once and shared with the internal publication contract
 * (`internal.ts`): both surfaces reach the same sanitizer, so a bound widened
 * for one and not the other would be a difference in what that sanitizer is
 * handed depending on which door the caller came through.
 */
export const DocRenderedHtmlSchema = z.string().max(2_000_000)

/**
 * Identifies the renderer that produced `renderedHtml`, so staleness is
 * QUERYABLE after a vocabulary change rather than invisible until a reader
 * notices a page looks wrong. Stored verbatim: only the renderer knows this, and
 * web and backend deploy separately, so a backend-owned constant would lie under
 * skew. Bounded, never semantically validated — it is metadata about the cache,
 * never content.
 */
export const DocRendererVersionSchema = z.string().max(64)

export const PublishDocRequestSchema = z
  .object({
    draftPublicId: z.string().min(1),
    renderedHtml: DocRenderedHtmlSchema.optional(),
    rendererVersion: DocRendererVersionSchema.optional()
  })
  .strict()
  .refine(value => value.rendererVersion === undefined || value.renderedHtml !== undefined, {
    message: 'rendererVersion requires renderedHtml',
    path: ['rendererVersion']
  })
export type PublishDocRequest = z.infer<typeof PublishDocRequestSchema>

export const UnpublishDocParamsSchema = DocIdParam
export type UnpublishDocParams = z.infer<typeof UnpublishDocParamsSchema>

/**
 * Rotate (i.e. REVOKE) a link-shareable page's token. A mutating super-admin
 * route like every other write in this domain (KTD4/R12).
 *
 * No body: rotation takes no input by design. A caller-supplied token would be
 * a caller-chosen secret, and the one property this tier rests on is that the
 * token comes from a CSPRNG and nowhere else.
 */
export const RotateDocShareTokenParamsSchema = DocIdParam
export type RotateDocShareTokenParams = z.infer<typeof RotateDocShareTokenParamsSchema>
